import React from 'react';
import { Card } from '../components/ui/card';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Search, Users, Zap, CheckCircle, DollarSign, BarChart } from 'lucide-react';

const ProcessPage = () => {
  const steps = [
    {
      number: 1,
      icon: Search,
      title: 'Define Requirements',
      description: 'Share your job description and requirements with our team',
      details: [
        'Technical skills needed',
        'Experience level',
        'Role responsibilities',
        'Timeline and budget'
      ]
    },
    {
      number: 2,
      icon: Zap,
      title: 'AI Matching',
      description: 'Our AI analyzes your needs and matches you with qualified candidates',
      details: [
        'Skills compatibility scoring',
        'Experience verification',
        'Availability matching',
        'Cultural fit assessment'
      ]
    },
    {
      number: 3,
      icon: Users,
      title: 'Review Candidates',
      description: 'Get a curated shortlist of pre-vetted candidates',
      details: [
        'AI interview results',
        'Portfolio and resume',
        'Performance predictions',
        'Reference checks'
      ]
    },
    {
      number: 4,
      icon: CheckCircle,
      title: 'Interview & Hire',
      description: 'Conduct interviews and make your decision',
      details: [
        'Schedule directly with candidates',
        'Technical assessments',
        'Final interviews',
        'Contract negotiation support'
      ]
    },
    {
      number: 5,
      icon: DollarSign,
      title: 'Onboard & Pay',
      description: 'Seamless onboarding and payment management',
      details: [
        'Automated contract generation',
        'Global payroll handling',
        'Time tracking integration',
        'Milestone-based payments'
      ]
    },
    {
      number: 6,
      icon: BarChart,
      title: 'Monitor & Optimize',
      description: 'Track performance and scale your team',
      details: [
        'Performance analytics',
        'Engagement metrics',
        'Continuous improvement',
        'Team expansion support'
      ]
    }
  ];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#F9FBF5] pt-20">
        <div className="max-w-7xl mx-auto px-6 py-16">
          {/* Hero */}
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Our Hiring Process
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From requirement to hire in 48 hours. See how Duty AI streamlines your recruitment.
            </p>
          </div>

          {/* Timeline */}
          <div className="relative mb-16">
            {/* Vertical line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200" />

            <div className="space-y-12">
              {steps.map((step) => {
                const Icon = step.icon;
                return (
                  <div key={step.number} className="relative pl-24">
                    {/* Circle */}
                    <div className="absolute left-0 w-16 h-16 bg-[#0A3900] rounded-full flex items-center justify-center">
                      <Icon className="w-8 h-8 text-white" />
                    </div>

                    <Card className="p-8">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <span className="text-sm font-medium text-[#0A3900] mb-2 block">
                            Step {step.number}
                          </span>
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            {step.title}
                          </h3>
                          <p className="text-gray-600">{step.description}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
                        {step.details.map((detail, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span>{detail}</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
            <Card className="p-6 text-center">
              <div className="text-3xl font-bold text-[#0A3900] mb-2">48hrs</div>
              <div className="text-sm text-gray-600">Time to first candidate</div>
            </Card>
            <Card className="p-6 text-center">
              <div className="text-3xl font-bold text-[#0A3900] mb-2">95%</div>
              <div className="text-sm text-gray-600">Candidate success rate</div>
            </Card>
            <Card className="p-6 text-center">
              <div className="text-3xl font-bold text-[#0A3900] mb-2">10x</div>
              <div className="text-sm text-gray-600">Faster than traditional hiring</div>
            </Card>
            <Card className="p-6 text-center">
              <div className="text-3xl font-bold text-[#0A3900] mb-2">$0</div>
              <div className="text-sm text-gray-600">Upfront costs</div>
            </Card>
          </div>

          {/* CTA */}
          <div className="bg-[#082900] rounded-2xl p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Streamline Your Hiring?</h2>
            <p className="text-lg mb-8 opacity-90">
              Start finding top talent today
            </p>
            <button
              className="bg-white text-[#0A3900] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              onClick={() => window.location.href = '/contact'}
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ProcessPage;